import {Component, Object3D, property, Type, WonderlandEngine} from '@wonderlandengine/api';

import {GameState, State} from './classes/gameState.js';
import {storageSpace13} from './globals.js';
import {LevelGenerator} from './level-generator.js';
import {FadeToBlack} from '@sorskoot/wonderland-components';
import {vec3} from 'gl-matrix';
import {UIModel, UIState} from './models/UIModel.js';
import {GameProgress, GameProgressItems} from './classes/GameProgress.js';

export class Game extends Component {
    static TypeName = 'game';
    // Singleton
    private static _instance: Game;
    static get instance(): Game {
        return Game._instance;
    }

    @property.object({required: true})
    levelGenObject!: Object3D;

    @property.object({required: true})
    playerObject!: Object3D;

    @property.object({required: true})
    blackBox!: Object3D;

    @property.object({required: true})
    fadeToBlackObject!: Object3D;

    @property.object({required: true})
    UIRoot!: Object3D;

    @property.object({required: true})
    cameraObject!: Object3D;

    levelGen: LevelGenerator;
    originalPlayerPosition: number[];

    uiModel: UIModel = new UIModel();

    private _fadeToBlack: FadeToBlack;
    private _wasPaused: boolean;

    init(): void {
        if (Game._instance !== undefined) {
            throw new Error('Game is a singleton and can only be used once!');
        }
        Game._instance = this;
    }

    start() {
        document.getElementById('afterLoading').style.display = 'block';

        this.uiModel.state = UIState.none;

        this.levelGen = this.levelGenObject.getComponent(LevelGenerator);

        //StorageSpace13.particlePool = new ObjectCache(this.engine, 'particles', 200, null, 400);

        this.engine.onXRSessionStart.add(() => (storageSpace13.gameState.isInVR = true));
        this.engine.onXRSessionEnd.add(() => (storageSpace13.gameState.isInVR = false));

        this.engine.onXRSessionStart.add(() => {
            storageSpace13.soundFxPlayer.initAudio();
            storageSpace13.musicPlayer.initAudio();
        });

        this._fadeToBlack = this.fadeToBlackObject.getComponent(FadeToBlack);

        storageSpace13.gameState.changeOnProperty('level').add((level) => {
            storageSpace13.levelState.initLevelState(level);

            const currentLevel = level as number;
            let result = this.levelGen.generate(currentLevel);
            GameProgress.instance.record(GameProgressItems.lastLevel, currentLevel);

            storageSpace13.gameState.playerRotation = result.cameraRotation;
            storageSpace13.gameState.playerPosition = result.cameraPosition;
            storageSpace13.gameState.availableTargets = result.targetsToComplete;
            storageSpace13.levelState.availableTargets = result.targetsToComplete;
        });

        storageSpace13.gameState.changeOnProperty('state').add((state, oldState) => {
            switch (state) {
                case State.Pause:
                    this._transitionToPause().catch((e) => console.log(e));
                    break;
                case State.Playing:
                    if (this._wasPaused) {
                        this._transitionFromPause().catch((e) => console.log(e));
                    } else {
                        Game.instance.uiModel.state = UIState.none;
                    }
                    break;
            }
        });

        window.addEventListener('keyup', (e) => {
            if (e.code == 'BracketLeft') {
                if (storageSpace13.gameState.level == 14) {
                    storageSpace13.gameState.state = State.Title;
                    storageSpace13.gameState.playerRotation = 0;
                } else {
                    //@ts-ignore
                    storageSpace13.gameState.level++;
                }
            }
            if (e.code == 'BracketRight') {
                storageSpace13.gameState.level = storageSpace13.gameState.level;
            }
        });
    }

    private async _transitionToPause() {
        this._wasPaused = true;
        await this._fadeToBlack.fadeToBlack();

        // move player to center of box
        const bbpos = this.blackBox.getPositionWorld([]);
        this.originalPlayerPosition = [...storageSpace13.gameState.playerPosition];
        storageSpace13.gameState.playerPosition = vec3.fromValues(bbpos[0], bbpos[1], bbpos[2]);
        this.UIRoot.setPositionWorld(bbpos);
        Game.instance.uiModel.state = UIState.pause;
        await this._fadeToBlack.fadeFromBlack();
    }

    private async _transitionFromPause() {
        await this._fadeToBlack.fadeToBlack();

        storageSpace13.gameState.playerPosition = vec3.fromValues(
            this.originalPlayerPosition[0],
            this.originalPlayerPosition[1],
            this.originalPlayerPosition[2]
        );
        this.UIRoot.setPositionWorld([0, 0, 0]);
        Game.instance.uiModel.state = UIState.none;
        await this._fadeToBlack.fadeFromBlack();
        this._wasPaused = false;
    }

    private _firstUpdate = false;

    update() {
        if (!this._firstUpdate) {
            this._firstUpdate = true;
            storageSpace13.gameState.state = State.Title;
            this.uiModel.state = UIState.title;
        }
    }
}
